import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CounterBadge, mergeClasses } from '@fluentui/react-components';
import { QuestionCircle20Regular, Emoji20Regular } from '@fluentui/react-icons';
import { useHeaderStyles } from './Header.styles';
import { HelpDrawer } from './HelpDrawer';
import { NotificationsDrawer } from './NotificationsDrawer';
import { Feedback } from './Feedback';
import { ProfileAvatar } from '@components/common/ProfileAvatar';
import { ThemeToggleButton } from '@components/common/ThemeToggleButton';
import { WaffleToggle } from '@components/common/WaffleToggle';
import { useWaffleToggleStyles } from '@components/common/WaffleToggle/WaffleToggle.styles';
import waffleIconUrl from '@components/common/WaffleToggle/waffle-icon.svg';
import { useIsMobile } from '@/hooks/useIsMobile';
import { usePersona } from '@/auth/usePersona';
import { useProfilePhoto } from '@/hooks/useProfilePhoto';
import { useRegionQuickLinks } from '@/hooks/useRegionQuickLinks';
import { ROUTES } from '@routes/routeConstants';
import type { RailState } from '@/types/leftNavRail.types';
import type { QuickLink } from '@store/models/booking';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { markAllRead, selectUnreadCount } from '@store/slices/notificationsSlice';
import { telemetry } from '@/services/telemetry';
import { TELEMETRY_COMPONENT, EVENT_TYPES, TELEMETRY_PROPERTIES } from '@/constants';
import { getFeedbackConfig } from '@config/feedbackConfig';
import notificationsIconUrl from '@assets/icons/header-notifications.svg';

export interface HeaderProps {
  appTitle?: string;
  displayName?: string;
  userEmail?: string;
  onProfileClick?: () => void;
  railState?: RailState;
  onToggleRail?: () => void;
  /** When provided, the WaffleToggle on mobile viewports opens the mobile nav drawer instead of toggling the rail. */
  onOpenMobileNav?: () => void;
  /**
   * Overrides the default {@link EXPOSE_NOTIFICATIONS} flag. Primarily used by
   * tests to exercise the Notifications icon/badge/drawer wiring while the
   * feature is hidden in production. Defaults to the module-level flag.
   */
  exposeNotifications?: boolean;
  /**
   * Overrides whether the Microsoft feedback (smiley) entry point is shown.
   * Defaults to whether an OCV `appId` is configured (see `feedbackConfig`).
   * Primarily used by tests to render the entry point without OCV provisioning.
   */
  exposeFeedback?: boolean;
}

type ActiveDrawer = 'help' | 'notifications' | null;

/**
 * spec 101: the Notifications icon is temporarily hidden in the header.
 * All wiring (slice, selectors, drawer component, openNotifications handler)
 * remains intact and tested below; flip this flag to `true` to expose the
 * icon again when the feature is ready to ship.
 */
const EXPOSE_NOTIFICATIONS = false;

export function Header({
  appTitle,
  displayName,
  userEmail,
  onProfileClick,
  railState,
  onToggleRail,
  onOpenMobileNav,
  exposeNotifications = EXPOSE_NOTIFICATIONS,
  exposeFeedback = getFeedbackConfig().isEnabled,
}: HeaderProps): React.JSX.Element {
  const styles = useHeaderStyles();
  const waffleStyles = useWaffleToggleStyles();
  const { t } = useTranslation();
  const { t: tHeader } = useTranslation('header');
  const navigate = useNavigate();
  const { isAdmin } = usePersona();
  const { photoUrl } = useProfilePhoto(userEmail);
  const isMobile = useIsMobile();
  const [activeDrawer, setActiveDrawer] = useState<ActiveDrawer>(null);
  const dispatch = useAppDispatch();
  const unreadCount = useAppSelector(selectUnreadCount);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  const defaultRoute = isAdmin ? ROUTES.ADMIN : ROUTES.BOOKINGS;
  const showDesktopWaffle = !isMobile && typeof onToggleRail === 'function';
  const showMobileWaffle = isMobile && typeof onOpenMobileNav === 'function';
  const showWaffle = showDesktopWaffle || showMobileWaffle;
  const quickLinks = useRegionQuickLinks();

  const handleQuickLinkClick = useCallback((link: QuickLink) => {
    telemetry.trackEvent(TELEMETRY_COMPONENT, EVENT_TYPES.BUTTON_CLICK, {
      [TELEMETRY_PROPERTIES.BUTTON_NAME]: 'Header.HelpPanel.QuickLink.Click',
      [TELEMETRY_PROPERTIES.LINK_NAME]: link.labelKey,
    });
  }, []);

  const openMobileNav = () => {
    if (!onOpenMobileNav) return;
    telemetry.trackEvent(TELEMETRY_COMPONENT, EVENT_TYPES.BUTTON_CLICK, {
      [TELEMETRY_PROPERTIES.BUTTON_NAME]: 'Header.MobileNav.Open',
      source: 'WaffleToggle',
    });
    onOpenMobileNav();
  };

  const openNotifications = () => {
    if (unreadCount > 0) {
      dispatch(markAllRead());
    }
    setActiveDrawer((d) => {
      const next = d === 'notifications' ? null : 'notifications';
      if (next === 'notifications') {
        telemetry.trackEvent(TELEMETRY_COMPONENT, EVENT_TYPES.BUTTON_CLICK, {
          [TELEMETRY_PROPERTIES.BUTTON_NAME]: 'Header.NotificationsPanel.Open',
        });
      }
      return next;
    });
  };

  const toggleHelp = () => {
    setActiveDrawer((d) => {
      const next = d === 'help' ? null : 'help';
      if (next === 'help') {
        telemetry.trackEvent(TELEMETRY_COMPONENT, EVENT_TYPES.BUTTON_CLICK, {
          [TELEMETRY_PROPERTIES.BUTTON_NAME]: 'Header.HelpPanel.Open',
        });
      }
      return next;
    });
  };

  const openFeedback = () => {
    setActiveDrawer(null);
    setFeedbackOpen(true);
    telemetry.trackEvent(TELEMETRY_COMPONENT, EVENT_TYPES.BUTTON_CLICK, {
      [TELEMETRY_PROPERTIES.BUTTON_NAME]: 'Header.Feedback.Open',
    });
  };

  return (
    <header
      className={mergeClasses(styles.root, showWaffle && styles.rootWithWaffle)}
      role="banner"
    >
      {showDesktopWaffle && onToggleRail && (
        <WaffleToggle state={railState ?? 'collapsed'} onToggle={onToggleRail} />
      )}
      {showMobileWaffle && (
        <button
          type="button"
          className={mergeClasses(waffleStyles.root, waffleStyles.rootCollapsed)}
          onClick={openMobileNav}
          data-testid="header-waffle-toggle"
          aria-label={t('leftNav.mobileMenu.open')}
        >
          <span className={waffleStyles.iconColumn} aria-hidden="true">
            <img src={waffleIconUrl} alt="" aria-hidden="true" className={waffleStyles.icon} />
          </span>
        </button>
      )}
      <button
        type="button"
        className={mergeClasses(styles.titleButton, showWaffle && styles.titleButtonWithWaffle)}
        onClick={() => void navigate(defaultRoute)}
      >
        {appTitle ?? t('header.appTitle')}
      </button>
      <div className={styles.commands}>
        <button
          type="button"
          aria-label={tHeader('help.aria')}
          data-testid="header-help-trigger"
          onClick={toggleHelp}
          className={styles.iconButton}
        >
          <QuestionCircle20Regular className={styles.icon} aria-hidden="true" />
        </button>
        {exposeFeedback && (
          <button
            type="button"
            aria-label={tHeader('feedback.aria')}
            data-testid="header-feedback-trigger"
            onClick={openFeedback}
            className={styles.iconButton}
          >
            <Emoji20Regular className={styles.icon} aria-hidden="true" />
          </button>
        )}
        {exposeNotifications && (
          <div className={styles.notificationsWrapper}>
            <button
              type="button"
              aria-label={tHeader('notifications.aria')}
              data-testid="header-notifications-trigger"
              onClick={openNotifications}
              className={styles.iconButton}
            >
              <img
                src={notificationsIconUrl}
                alt=""
                aria-hidden="true"
                className={styles.iconImg}
              />
            </button>
            {unreadCount > 0 && (
              <CounterBadge
                count={unreadCount}
                overflowCount={99}
                appearance="filled"
                color="danger"
                size="small"
                className={styles.badge}
                data-testid="header-notifications-badge"
              />
            )}
          </div>
        )}
        <ThemeToggleButton />
        <ProfileAvatar
          displayName={displayName}
          photoUrl={photoUrl ?? undefined}
          onClick={onProfileClick}
        />
      </div>
      <HelpDrawer
        open={activeDrawer === 'help'}
        onOpenChange={(open) => setActiveDrawer(open ? 'help' : null)}
        quickLinks={quickLinks}
        onLinkClick={handleQuickLinkClick}
      />
      <NotificationsDrawer
        open={activeDrawer === 'notifications'}
        onOpenChange={(open) => setActiveDrawer(open ? 'notifications' : null)}
      />
      {feedbackOpen && <Feedback setIsFeedbackModalOpen={setFeedbackOpen} />}
    </header>
  );
}
